import { type FC, type KeyboardEvent } from "react";
import ChatBubbleIcon from "./icons/ChatBubbleIcon";
import { useWidget } from "../contexts/useWidget";
import { useCore } from "../contexts/useCore";
import { Slot, Templated, runSlotAction } from "../templating";
import person from "../assets/person.png";

/**
 * v2 — no props. `onOpen` was removed in favor of the registry's
 * `toggle-button` interactive slot: clicking dispatches `toggle:click` on
 * `<lastbot-chat>` and, if not prevented, calls `core.open()`.
 */
const ChatToggleButton: FC = () => {
  const { brandInformation } = useWidget();
  const core = useCore();
  const avatarUrl = brandInformation?.avatar;
  const showAvatar = brandInformation?.show_avatar_in_minimized;

  const handleToggle = (): void => runSlotAction(core, "toggle-button");

  // Keyboard activation guard for non-button templated roots. Native <button>
  // already maps Enter/Space to a click event, so skip when the root is a
  // button to avoid double-firing the action.
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.currentTarget.tagName === "BUTTON") return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleToggle();
    }
  };

  return (
    <Templated
      id="lastbot-toggle-button"
      rootProps={{
        type: "button",
        onClick: handleToggle,
        onKeyDown: handleKeyDown,
        tabIndex: 0,
        role: "button",
        "data-testid": "chat-toggle",
      }}
    >
      <button
        type="button"
        onClick={handleToggle}
        data-testid="chat-toggle"
        className={`lastbot-chat-toggle cursor-pointer text-lastbot-on-primary font-semibold transition-colors duration-200 rounded-full shadow-lg ${showAvatar ? 'p-1' : 'p-4'} bg-linear-to-r from-lastbot-primary to-lastbot-primary-accent hover:from-lastbot-primary-strong hover:to-lastbot-primary-accent-strong`}
      >
        <Slot name="toggle-icon">
          {showAvatar ? (
            <img src={avatarUrl || person} alt="Chat" className="size-11 rounded-full object-cover" />
          ) : (
            <ChatBubbleIcon />
          )}
        </Slot>
      </button>
    </Templated>
  );
};

export default ChatToggleButton;
